import { animated, useSpring } from '@react-spring/web';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { ConditionalAccess } from '../../../../../admin/components/ConditionalAccess';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { getDisplayName } from '../../../../../models/NameUtils';
import { copyToClipboard, getDefaultDateStringValue } from '../../../../../models/UiUtils';
import { invalidateAllInfiniteCampaignReportQueries, useDeleteBulkEmailMutation } from '../../../../../queries';
import { useCampaignGroupReportStatsQuery } from '../../../../../queries/Reports/useCampaignGroupReportStatsQuery';
import { AdminPermission } from '../../../../../viewmodels/AdminViewModels';
import { Contacts } from '../../../../containers/reporting/Campaigns/Contacts';
import { brandPrimary } from '../../../../styles/colors';
import { baseStyleSheet, bs } from '../../../../styles/styles';
import { Checkbox } from '../../../Checkbox';
import { ClipboardIcon } from '../../../svgs/icons/ClipboardIcon';
import { CollapseIcon } from '../../../svgs/icons/CollapseIcon';
import { ExpandIcon } from '../../../svgs/icons/ExpandIcon';
import { ContactFilterRequestTagList } from '../GroupEmailTableRow/ContactFilterRequestTagList';
import { styleSheet as rowStyleSheet } from '../GroupEmailTableRow/styles';
import { styleSheet as rowAnalyticsStyleSheet } from '../GroupEmailTableRowAnalytics/styles';
import { GroupEmailTableRowExpandableStatusDisplay } from '../GroupEmailTableRowStatus';
import { RowTagFilterShortcut } from '../RowTagFilterShortcut';
import { GroupEmailTableRows } from './presentation';
import { styleSheet } from './styles';
import { useCampaignGroup } from './useCampaignGroup';

interface IProps {
	campaigns: Api.ICampaign[];
	onCancelClicked?(campaignToCancel?: Api.ICampaign): (e: React.MouseEvent<HTMLElement>) => void;
	onClick?(campaign: Api.ICampaign): void;
	onEditClicked?(campaignToEdit?: Api.ICampaign): void;
	onRenderApprovalActions?(campaign?: Api.ICampaign): () => JSX.Element;
	onRescheduleClicked?(campaignToReschedule?: Api.ICampaign): void;
	onCheckChanged?: (checked: boolean) => void;
	editGroupClicked?: (groupId: string) => void;
	impersonationContext?: Api.IImpersonationContext;
}

export function GroupEmailTableRowExpandable({
	onClick,
	onRescheduleClicked,
	onRenderApprovalActions,
	onEditClicked,
	onCancelClicked,
	campaigns,
	onCheckChanged,
	editGroupClicked,
	impersonationContext,
}: IProps) {
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('GroupEmailTableRowExpandable');
	const userSession = useUserSession();
	const [isExpanded, setIsExpanded] = React.useState(false);
	const [hasBeenEngaged, setHasBeenEngaged] = React.useState(false);
	const [checked, setChecked] = React.useState(false);
	const [isHovered, setIsHovered] = React.useState(false);

	const spring = useSpring({
		config: { duration: Math.max(Math.min(500, 75 * campaigns?.length), 200) },
		from: {
			height: isExpanded ? 0 : 'auto',
			opacity: isExpanded ? 0 : 1,
			overflow: isExpanded ? 'hidden' : 'visible',
			top: isExpanded ? -50 : 0,
		},
		to: {
			height: isExpanded ? 'auto' : 0,
			opacity: isExpanded ? 1 : 0,
			overflow: isExpanded ? 'visible' : 'hidden',
			top: isExpanded ? 0 : -50,
		},
	});
	const { earliestStartDate, groupData, groupId, groupStats } = useCampaignGroup({ campaigns });
	const isHtmlNewsletter = campaigns.some(x => x.categories?.includes(Api.EmailCategories.HtmlNewsletter));

	const [selectedTagFilter, setSelectedTagFilter] = React.useState<Api.IAccountTag>();
	const isAdmin = /admin/i.test(userSession.user?.role || '');
	const focusCampaignId = React.useMemo(() => {
		const withCreationDate = campaigns.filter(c => !!c.id && !!c.creationDate);
		if (withCreationDate.length === 0) {
			return campaigns.find(c => !!c.id)?.id;
		}
		return [...withCreationDate].sort(
			(a, b) => new Date(a.creationDate).getTime() - new Date(b.creationDate).getTime()
		)[0]?.id;
	}, [campaigns]);
	const tagFilterNames = selectedTagFilter?.name ? [selectedTagFilter.name] : [];
	const groupStatsQuery = useCampaignGroupReportStatsQuery({
		campaignId: focusCampaignId ?? '',
		tags: tagFilterNames,
		enabled: isAdmin && !!focusCampaignId && tagFilterNames.length > 0,
	});
	const filteredGroup = selectedTagFilter ? groupStatsQuery.data : undefined;
	const displayGroupStats = React.useMemo(() => {
		if (!filteredGroup) {
			return groupStats;
		}
		const emailed = filteredGroup.sentSuccessfullyCount ?? 0;
		const viewed = filteredGroup.openCount ?? 0;
		const replied = filteredGroup.replyCount ?? 0;
		const failed = filteredGroup.failedCount ?? 0;
		const noEmail = filteredGroup.noEmailAddressCount ?? 0;
		const stats: { count: number; message: string }[] = [
			{ count: emailed, message: 'emailed' },
			{ count: viewed, message: 'viewed' },
		];
		if (replied > 0) stats.push({ count: replied, message: 'replied' });
		if (failed > 0) stats.push({ count: failed, message: 'failed' });
		if (noEmail > 0) stats.push({ count: noEmail, message: 'no email' });
		return stats;
	}, [filteredGroup, groupStats]);

	/**
	 * Should only show analytics if all campaigns in group are either SENDING or COMPLETE. if at least one campaign has
	 * a different status, aggregated data should not show analytics
	 */
	const showAnalytics =
		campaigns?.filter(
			campaign => campaign.status !== Api.EmailSendStatus.Sending && campaign.status !== Api.EmailSendStatus.Complete
		).length === 0;
	const sampleContacts = campaigns.reduce(
		(acc, curr) => [...acc, ...(curr.sampleContacts ?? []).map(c => new Api.CampaignViewModel(userSession, c))],
		[]
	);
	/**
	 * If at least one campaign is still scheduled to send, aggregated data should show "SCHEDULED". only if all owners
	 * have cancelled should the aggregated data show as "CANCELLED".
	 */
	const isScheduledOrWaitingForApproval = React.useMemo(() => {
		return (
			campaigns.filter(
				campaign =>
					campaign.status === Api.EmailSendStatus.WaitingForApproval || campaign.status === Api.EmailSendStatus.Queued
			).length > 0
		);
	}, [campaigns]);

	const deleteBulkEmailMutation = useDeleteBulkEmailMutation({
		onSuccess: () => {
			toast.success('Campaign group cancelled successfully');
			invalidateAllInfiniteCampaignReportQueries();
		},
		onError: error => {
			logApiError('BulkEmail-Delete', error);
			errorMessages.pushApiError(error);
		},
	});
	const handleDeleteBulkEmail = (id: string) => {
		deleteBulkEmailMutation.mutate({
			id,
			impersonationContext,
		});
	};
	const onToggleExpand = (e: React.MouseEvent<HTMLElement>) => {
		e?.preventDefault();
		e?.stopPropagation();
		setHasBeenEngaged(true);
		setIsExpanded(!isExpanded);
	};
	const handleCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const _checked = e.target.checked;
		setChecked(_checked);
		onCheckChanged(_checked);
	};
	const handleCopyId = (ev: React.MouseEvent<HTMLElement>) => {
		ev.stopPropagation();
		copyToClipboard(groupId ?? '');
		toast.success('Group ID copied to clipboard');
	};
	const getCampaignRowCallbacks = (campaign: Api.ICampaign) => {
		return {
			onCancelClicked: onCancelClicked?.(campaign),
			onClick: () => onClick?.(campaign),
			onEditClicked: (ev?: React.MouseEvent<HTMLElement>) => {
				ev?.preventDefault();
				ev?.stopPropagation();
				onEditClicked?.(campaign);
			},
			onRenderApprovalActions: onRenderApprovalActions?.(campaign),
			onRescheduleClicked: (ev?: React.MouseEvent<HTMLElement>) => {
				ev?.preventDefault();
				ev?.stopPropagation();
				onRescheduleClicked?.(campaign);
			},
		};
	};
	const renderSentByInfo = () => {
		const campaign = campaigns[0];
		const cancelledCampaigns = campaigns.filter(x => x.status === Api.EmailSendStatus.Cancelled);
		const isCancelled = cancelledCampaigns.length === campaigns.length;
		const cancelDate = campaign.cancelledDate ? new Date(campaign.cancelledDate) : null;
		const completedDate = campaign.completedDate ? new Date(campaign.completedDate) : null;
		const SENT_BY_TEXT =
			campaigns.length > 1
				? 'All Campaign Owners'
				: (getDisplayName(campaign.creator as Api.IPrincipal) ?? '[Missing]');
		if (isCancelled) {
			return (
				<GroupEmailTableRowExpandableStatusDisplay
					actor={campaign.actor?.name ?? ''}
					campaign={campaign}
					date={cancelDate}
					color={rowStyleSheet.red}
					status='Cancelled'
					sentBy={SENT_BY_TEXT}
				/>
			);
		}
		if (campaign.approvalRequests?.length > 0) {
			const approval = campaign.approvalRequests?.[0];
			const isRejected = approval.response?.status === Api.CampaignApprovalStatus.Rejected;
			const isApproved = approval.response?.status === Api.CampaignApprovalStatus.Approved;
			return (
				<GroupEmailTableRowExpandableStatusDisplay
					actor={campaign.actor?.name ?? ''}
					campaign={campaign}
					color={isRejected ? rowStyleSheet.red : isApproved ? rowStyleSheet.blue : rowStyleSheet.orange}
					date={earliestStartDate ?? new Date(approval.response?.creationDate)}
					datePrefix={!isRejected && isApproved ? 'for' : ''}
					sentBy={SENT_BY_TEXT}
					status={isRejected ? 'Rejected' : isApproved ? 'Scheduled' : 'Pending approval'}
				/>
			);
		}
		if (campaign.status === Api.EmailSendStatus.Sending) {
			return (
				<GroupEmailTableRowExpandableStatusDisplay
					actor={campaign.actor?.name ?? ''}
					campaign={campaign}
					color={rowStyleSheet.blue}
					date={earliestStartDate}
					sentBy={SENT_BY_TEXT}
					status='Sending'
				/>
			);
		}
		return (
			<GroupEmailTableRowExpandableStatusDisplay
				actor={campaign.actor?.name ?? ''}
				campaign={campaign}
				color={completedDate ? rowStyleSheet.green : rowStyleSheet.blue}
				date={completedDate ?? earliestStartDate}
				datePrefix={!completedDate ? 'for' : ''}
				sentBy={SENT_BY_TEXT}
				status={completedDate ? 'Sent' : 'Scheduled'}
			/>
		);
	};
	const renderStatus = () => {
		const currentStatus = groupData.status;
		let title = '';
		let dateStringValue = '';

		switch (currentStatus) {
			case Api.EmailSendStatus.Complete: {
				title = 'Sent';
				dateStringValue = getDefaultDateStringValue(earliestStartDate);
				break;
			}
			case Api.EmailSendStatus.Cancelled: {
				title = 'Cancelled';
				break;
			}
			case Api.EmailSendStatus.Sending: {
				title = 'Sending';
				dateStringValue = getDefaultDateStringValue(earliestStartDate);
				break;
			}
			case Api.EmailSendStatus.Ready:
			default: {
				title = 'Scheduled';
				dateStringValue = getDefaultDateStringValue(earliestStartDate);
				break;
			}
		}

		return (
			<div className={css(rowStyleSheet.scheduleInfo)}>
				<div className={css(rowStyleSheet.scheduleInfoCount)}>{groupData.totalCount}</div>
				{title && (
					<div className={css(rowStyleSheet.scheduleInfoDate)}>
						<div className={css(rowStyleSheet.scheduleInfoDateTitle)}>{title}</div>
						{dateStringValue && <div>{dateStringValue}</div>}
					</div>
				)}
			</div>
		);
	};
	return (
		<div>
			<div
				className={css(rowStyleSheet.row, styleSheet.row, isExpanded && styleSheet.rowExpanded)}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
			>
				{onCheckChanged ? (
					<div className={css(rowStyleSheet.checkboxCol)}>
						<Checkbox
							id={`campaign-checkbox-${campaigns[0]?.id}`}
							checked={checked}
							onChange={handleCheckChange}
							onClick={e => {
								e.stopPropagation();
							}}
						/>
					</div>
				) : null}
				<div className={css(rowStyleSheet.subjectCell, styleSheet.subjectCell)} onClick={onToggleExpand}>
					<span className={css(rowStyleSheet.subject)}>
						<span className={css(styleSheet.subjectTitle)}>
							<button className={css(styleSheet.icon)} onClick={onToggleExpand}>
								{isExpanded ? <ExpandIcon fill={brandPrimary} /> : <CollapseIcon fill={brandPrimary} />}
							</button>
							<div className={css(baseStyleSheet.brandLink, baseStyleSheet.truncateText)}>{campaigns[0]?.subject}</div>
						</span>
						<ConditionalAccess permission={AdminPermission.OnlyForDevelopers}>
							<span onClick={handleCopyId}>
								<ClipboardIcon className={css(rowStyleSheet.linkIcon)} />
							</span>
						</ConditionalAccess>
					</span>
					{renderSentByInfo()}
				</div>
				{campaigns[0].filterRequest?.criteria ? (
					<ContactFilterRequestTagList filterRequest={campaigns[0].filterRequest} maxTagsToDisplayInline={2} />
				) : null}
				{!campaigns[0].filterRequest?.criteria && sampleContacts?.length ? (
					<div className={css(rowStyleSheet.contacts)}>
						<Contacts maxContacts={3} totalContacts={groupData.sentCount} contacts={sampleContacts} />
					</div>
				) : null}
				{!campaigns[0].filterRequest?.criteria && !sampleContacts?.length && groupData.totalCount ? (
					<div className={css(rowStyleSheet.contacts)}>
						<span className={css(rowStyleSheet.contactsCount)}>
							{groupData.totalCount} contact{groupData.totalCount > 1 ? 's' : ''}
						</span>
					</div>
				) : null}
				<div className={css(rowStyleSheet.analytics)}>
					<div className={css(rowStyleSheet.analyticsContent)}>
						{showAnalytics ? (
							<div className={css(rowStyleSheet.analyticsCountWrapper)}>
								{displayGroupStats?.map(s => (
									<div className={css(bs.p2)} key={s.message}>
										<div
											className={css(
												rowStyleSheet.count,
												(s.message === 'failed' || s.message === 'unsubscribed') && s.count > 0
													? rowAnalyticsStyleSheet.countFailed
													: null
											)}
										>
											{s.count.toLocaleString()}
										</div>
										<div className={css(rowStyleSheet.action)}>{s.message}</div>
									</div>
								))}
							</div>
						) : (
							<div className={css(rowStyleSheet.schedule)}>
								{renderStatus()}
								{groupData.status === Api.EmailSendStatus.Cancelled && groupData.sentCount > 0 ? (
									<div>
										<div className={css(rowStyleSheet.count)}>{groupData.sentCount.toLocaleString()}</div>
										<div className={css(rowStyleSheet.action)}>emailed</div>
									</div>
								) : null}
							</div>
						)}
					</div>
				</div>
				<div className={css(bs.gap4, styleSheet.expandedRowActions)}>
					{isAdmin && showAnalytics && focusCampaignId ? (
						<RowTagFilterShortcut
							selectedTag={selectedTagFilter}
							onTagSelected={setSelectedTagFilter}
							isVisible={isHovered}
							onTagCleared={() => setSelectedTagFilter(undefined)}
						/>
					) : null}
					{!isHtmlNewsletter &&
					userSession.canSendOnBehalf &&
					isScheduledOrWaitingForApproval &&
					groupId != null &&
					editGroupClicked ? (
						<button
							type='button'
							className={css(bs.brandLink, bs.whiteSpaceNoWrap)}
							onClick={() => {
								editGroupClicked(groupId);
							}}
						>
							Edit All
						</button>
					) : null}
					{groupData.status === Api.EmailSendStatus.Ready && userSession.canSendOnBehalf ? (
						<button
							className={css(bs.brandLink, bs.whiteSpaceNoWrap)}
							disabled={deleteBulkEmailMutation.isPending}
							onClick={() => handleDeleteBulkEmail(groupId)}
						>
							Cancel All
						</button>
					) : null}
				</div>
			</div>
			{hasBeenEngaged && (
				<animated.div className={css(styleSheet.expandable)} style={spring}>
					<div>
						<GroupEmailTableRows
							campaigns={campaigns}
							impersonationContext={impersonationContext}
							onRequestCallbacksForCampaign={getCampaignRowCallbacks}
						/>
					</div>
				</animated.div>
			)}
		</div>
	);
}
